import type {
  OnboardingCatalogItem,
  Subscription,
  SubscriptionPlan,
} from 'types';
import { SubscriptionFrequency } from 'types';

import { axiosInstance as axios } from './index';
import type { IpGeolocationResponse } from './geolocation';

export type CreateStripeCheckoutSessionBody = {
  subscriptionPlanId: string;
  frequency: SubscriptionFrequency;
  /** ISO-3166 alpha-2; aligns Checkout currency with GET `plans/v2` (default CH if omitted). */
  country_code?: string | null;
  /** QA override - same as catalog query. */
  presentment_currency?: 'CHF' | 'EUR' | 'USD' | null;
};

export type CreateStripeCheckoutSessionResponse = {
  url: string;
  sessionId: string;
  /** Subset of subscription plan fields from the API. */
  plan: Partial<SubscriptionPlan> & Record<string, unknown>;
};

export type CreateOnboardingCheckoutSessionBody = {
  onboardingPlanId: string;
  country_code?: string | null;
  presentment_currency?: 'CHF' | 'EUR' | 'USD' | null;
};

export type CreateOnboardingCheckoutSessionResponse = {
  url: string;
  sessionId: string;
  onboardingPlan: Record<string, unknown>;
};

export type CreateInvoiceSubscriptionBody = {
  subscriptionPlanId: string;
  payerEmails: string[];
  country_code?: string | null;
  presentment_currency?: 'CHF' | 'EUR' | 'USD' | null;
};

export type CreateInvoiceSubscriptionResponse = {
  subscription: Subscription;
  hostedInvoiceUrl: string | null;
  amountLabel?: string | null;
};

export type CreateEnterpriseRequestBody = {
  user_id: string;
  email: string;
  organization_name?: string;
  plan: 'enterprise';
  country_code?: string | null;
  presentment_currency?: 'CHF' | 'EUR' | 'USD' | null;
};

export type CreateEnterpriseRequestResponse = {
  id: string;
  stripeCustomerId: string;
  stripeInvoiceId: string;
  invoiceStatus: string;
};

export type ChangePlanPreviewParams = {
  subscriptionPlanId: string;
  frequency: SubscriptionFrequency;
};

export type ChangePlanPreviewPlan = Partial<SubscriptionPlan> & {
  frequency?: string;
  priceId?: string;
};

export type ChangePlanPreviewLineItem = {
  description?: string | null;
  amount?: number | null;
  currency?: string;
  proration?: boolean;
  period?: { start?: number; end?: number };
};

export type ChangePlanPreviewPricingBreakdown = {
  currency: string;
  actualPlanPrice: number;
  proratedChargeAmount: number;
  proratedCreditAmount: number;
  proratedBalance: number;
  amountDue: number;
};

export type ChangePlanPreviewResponse = {
  amountDue: number;
  currency?: string;
  creditAmount?: number;
  newPlanFullPrice?: number;
  pricingBreakdown?: ChangePlanPreviewPricingBreakdown;
  renewalDate?: string | null;
  subtotal?: number;
  tax?: number | null;
  prorationDate?: number;
  newPlan?: ChangePlanPreviewPlan;
  currentPlan?: ChangePlanPreviewPlan | null;
  prorationLineItems?: ChangePlanPreviewLineItem[];
  lineItems?: ChangePlanPreviewLineItem[];
};

export type ChangePlanBody = {
  subscriptionPlanId: string;
  frequency: SubscriptionFrequency;
  paymentMethod?: 'card' | 'invoice';
};

export type ChangePlanResponse = {
  checkoutUrl?: string | null;
  invoiceAmountDue?: number | null;
  invoiceCurrency?: string | null;
};

export const getSubscriptionsRequest = async (parameters?: string) => {
  const response = await axios.get(`subscriptions${parameters || '?new=true'}`);
  return response.data;
};

/**
 * Builds the query string for GET `/subscriptions/plans/v2`.
 * Sends ISO-2 `country_code` from geo (`country_code2`) for presentment (CHF / EUR / USD).
 * Falls back to CH when geo does not return a valid code (aligned with backend default).
 */
export const buildSubscriptionPlansCatalogQuery = (
  location: IpGeolocationResponse | null | undefined,
): string => {
  const params = new URLSearchParams();
  const code = location?.country_code2?.trim().toUpperCase();
  if (code && /^[A-Z]{2}$/.test(code)) {
    params.set('country_code', code);
  }
  if (!params.has('country_code')) {
    params.set('country_code', 'CH');
  }
  const query = params.toString();
  return query ? `?${query}` : '';
};

/** Same `country_code` / `presentment_currency` rules as catalog - for Stripe Checkout session APIs. */
export const buildStripeCheckoutPresentmentBody = (
  location: Pick<IpGeolocationResponse, 'country_code2'> | null | undefined,
): Pick<
  CreateStripeCheckoutSessionBody,
  'country_code' | 'presentment_currency'
> => {
  const code = location?.country_code2?.trim().toUpperCase();
  const country_code = code && /^[A-Z]{2}$/.test(code) ? code : 'CH';
  const override =
    process.env.REACT_APP_SUBSCRIPTION_PLANS_CURRENCY?.trim().toUpperCase();
  if (override === 'CHF' || override === 'EUR' || override === 'USD') {
    return {
      country_code,
      presentment_currency: override as 'CHF' | 'EUR' | 'USD',
    };
  }
  return { country_code };
};

/**
 * Optional dev/QA override: set `REACT_APP_SUBSCRIPTION_PLANS_CURRENCY` to `CHF`, `EUR`, or `USD`
 * to send `presentment_currency` (does not affect which DB plan rows load).
 */
export type SubscriptionPlansV2Response = {
  data: SubscriptionPlan[];
  onboarding?: OnboardingCatalogItem[];
};

/**
 * Normalizes `subscriptions/plans/v2` body (or legacy array-only payload).
 */
export const parseSubscriptionPlansV2Response = (
  raw: unknown,
): { data: SubscriptionPlan[]; onboarding: OnboardingCatalogItem[] } => {
  if (Array.isArray(raw)) {
    return { data: raw as SubscriptionPlan[], onboarding: [] };
  }
  if (raw && typeof raw === 'object') {
    const body = raw as Record<string, unknown>;
    const data = Array.isArray(body.data)
      ? (body.data as SubscriptionPlan[])
      : [];
    const onboarding = Array.isArray(body.onboarding)
      ? (body.onboarding as OnboardingCatalogItem[])
      : [];
    return { data, onboarding };
  }
  return { data: [], onboarding: [] };
};

export const getPricingPlansV2Request = async (
  parameters?: string,
): Promise<SubscriptionPlansV2Response> => {
  const qs = new URLSearchParams();
  const raw = parameters?.trim() ?? '';
  if (raw) {
    const withoutLeadingQuestion = raw.startsWith('?') ? raw.slice(1) : raw;
    new URLSearchParams(withoutLeadingQuestion).forEach((value, key) => {
      qs.set(key, value);
    });
  }
  const override =
    process.env.REACT_APP_SUBSCRIPTION_PLANS_CURRENCY?.trim().toUpperCase();
  if (override === 'CHF' || override === 'EUR' || override === 'USD') {
    qs.set('presentment_currency', override);
  }
  const query = qs.toString();
  const suffix = query ? `?${query}` : '';
  const response = await axios.get(`subscriptions/plans/v2${suffix}`);
  return response.data as SubscriptionPlansV2Response;
};

export const getSubscriptionPlanIdRequest = async (parameters: string) => {
  const response = await axios.get(`subscriptions/plan${parameters || ''}`);
  return response.data;
};

/** Stripe Checkout (hosted page) - redirect browser to `url`. */
export const createStripeCheckoutSessionRequest = async (
  body: CreateStripeCheckoutSessionBody,
) => {
  const response = await axios.post<CreateStripeCheckoutSessionResponse>(
    'subscriptions/stripe/checkout-session',
    body,
  );
  return response.data;
};

export const createOnboardingCheckoutSessionRequest = async (
  body: CreateOnboardingCheckoutSessionBody,
) => {
  const response = await axios.post<CreateOnboardingCheckoutSessionResponse>(
    'subscriptions/stripe/onboarding-checkout-session',
    body,
  );
  return response.data;
};

export const setSubscriptionRequest = async ({
  productId,
  subscriptionId,
  pricing,
  country,
}: {
  productId: string;
  subscriptionId: string;
  pricing: string;
  country: string;
}) => {
  const response = await axios.post('subscriptions', {
    country,
    frequency: pricing,
    subscriptionPlanId: productId,
    subscriptionId,
  });
  return response.data;
};

export const startTrialRequest = () => {
  return axios.get('subscriptions/startTrial');
};

export const activateSubscriptionRequest = (subscriptionId: string) => {
  return axios.post('subscriptions/activateSubscription', {
    subscriptionId,
  });
};

export const cancelSubscriptionRequest = (subscriptionId: string) => {
  return axios.post('subscriptions/stripe/cancel', {
    subscriptionId,
  });
};

/** Yearly plan billed by Stripe invoice; payer emails stored on the subscription row. */
export const createInvoiceSubscriptionRequest = async (
  body: CreateInvoiceSubscriptionBody,
) => {
  const response = await axios.post<CreateInvoiceSubscriptionResponse>(
    'subscriptions/stripe/invoice-subscription',
    body,
  );
  return response.data;
};

/** Enterprise request flow (draft invoice, sales follow-up). */
export const createEnterpriseRequest = async (
  body: CreateEnterpriseRequestBody,
) => {
  const response = await axios.post<CreateEnterpriseRequestResponse>(
    'subscriptions/enterprise/request',
    body,
  );
  return response.data;
};

/** Existing subscriber changing plan: prorated amount due (GET, parallel-safe). */
export const getChangePlanPreviewRequest = async (
  params: ChangePlanPreviewParams,
): Promise<ChangePlanPreviewResponse> => {
  const response = await axios.get<ChangePlanPreviewResponse>(
    'subscriptions/stripe/change-plan/preview',
    { params },
  );
  return response.data;
};

/** Existing subscriber changing plan: Stripe checkout or invoice (POST on pay). */
export const postChangePlanRequest = async (
  body: ChangePlanBody,
): Promise<ChangePlanResponse> => {
  const response = await axios.post<ChangePlanResponse>(
    'subscriptions/stripe/change-plan',
    body,
  );
  return response.data;
};

export type PatchSubscriptionCollectionMethodBody = {
  method: 'invoice' | 'card';
};

export type PatchSubscriptionCollectionMethodResponse = {
  addCardRequired?: boolean;
  addCardUrl?: string | null;
};

export type CreateAddCardSessionResponse = {
  url: string;
  sessionId?: string;
};

export const patchSubscriptionCollectionMethodRequest = async (
  body: PatchSubscriptionCollectionMethodBody,
): Promise<PatchSubscriptionCollectionMethodResponse> => {
  const response = await axios.patch<PatchSubscriptionCollectionMethodResponse>(
    'subscription/collection-method',
    body,
  );
  return response.data;
};

/** Stripe Setup session when switching invoice → card without a card on file. */
export const createAddCardSessionRequest = async () => {
  const response = await axios.post<CreateAddCardSessionResponse>(
    'subscriptions/stripe/add-card-session',
    {},
  );
  return response.data;
};
